/**
 * @file Alert 页内提示条
 * @description 信息 / 成功 / 警告 / 危险提示。
 *
 * @whenToUse 仅 `/showcase` 预览本控件；业务页面不要使用。
 * @whenNotToUse 任何操作反馈（成功/失败/校验提示）必须用 Toast；空列表用 Empty。
 */

import type { HTMLAttributes, ReactNode } from 'react'
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type AlertVariant = 'info' | 'success' | 'warning' | 'danger'

export type AlertProps = HTMLAttributes<HTMLDivElement> & {
  variant?: AlertVariant
  /** 标题 */
  title?: ReactNode
  children?: ReactNode
}

const variantClass: Record<AlertVariant, string> = {
  info: 'border-info/40 bg-info/10 text-info',
  success: 'border-success/40 bg-success/10 text-success',
  warning: 'border-warning/40 bg-warning/10 text-warning',
  danger: 'border-danger/40 bg-danger/10 text-danger',
}

const icons: Record<AlertVariant, ReactNode> = {
  info: <Info className="size-4 shrink-0 text-info" aria-hidden />,
  success: <CircleCheck className="size-4 shrink-0 text-success" aria-hidden />,
  warning: <TriangleAlert className="size-4 shrink-0 text-warning" aria-hidden />,
  danger: <CircleAlert className="size-4 shrink-0 text-danger" aria-hidden />,
}

export function Alert({
  variant = 'info',
  title,
  className,
  children,
  ...rest
}: AlertProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex gap-2 rounded-lg border px-3 py-2 text-sm',
        variantClass[variant],
        className,
      )}
      {...rest}
    >
      {icons[variant]}
      <div className="min-w-0 flex-1 space-y-1">
        {title ? <div className="font-medium">{title}</div> : null}
        {children ? <div className="text-muted">{children}</div> : null}
      </div>
    </div>
  )
}
